import { Description, ErrorMessage, FieldError, Input, Label, TextField } from "@heroui/react";
import type { ReactNode } from "react";
import { type Control, Controller, type FieldPath, type FieldValues } from "react-hook-form";
import { clsx } from "@/src/common/utils/clsx";

interface InputGroupProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> {
	control: Control<TFieldValues>;
	name: TName;
	label?: ReactNode;
	help?: ReactNode;
	placeholder?: string;
	type?: React.HTMLInputTypeAttribute;
	isRequired?: boolean;
	autoComplete?: string;
	inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
	className?: string;
	onClick?: React.MouseEventHandler<HTMLInputElement>;
}

/**
 * RHF-aware input group: label + input + help text + inline field error.
 * All domain forms should use this instead of raw HeroUI `TextField`.
 */
export function InputGroup<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
	control,
	name,
	label,
	help,
	placeholder,
	type = "text",
	isRequired,
	autoComplete,
	inputMode,
	className,
	onClick,
}: InputGroupProps<TFieldValues, TName>) {
	return (
		<Controller
			control={control}
			name={name}
			render={({ field, fieldState }) => (
				<TextField
					className={clsx("flex w-full flex-col gap-1.5", className)}
					isInvalid={Boolean(fieldState.error)}
					isRequired={isRequired}
					value={(field.value as string | undefined) ?? ""}
					onChange={field.onChange}
					onBlur={field.onBlur}
					name={field.name}
				>
					{label ? <Label>{label}</Label> : null}
					<Input
						ref={field.ref}
						placeholder={placeholder}
						type={type}
						autoComplete={autoComplete}
						inputMode={inputMode}
						onClick={onClick}
					/>
					{help ? <Description>{help}</Description> : null}
					<FieldError>
						<ErrorMessage>{fieldState.error?.message}</ErrorMessage>
					</FieldError>
				</TextField>
			)}
		/>
	);
}
